// ===== Reviews Console Page (Backend Integrated) =====
import React from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { StarIcon, ChatBubbleLeftIcon, FlagIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const ReviewsConsolePage = () => {
  const { reviews, loading, refreshData } = useVenueDashboard();

  // Calculate stats from real data
  const averageRating = reviews && reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : '0.0';
  
  const flaggedReviews = reviews?.filter(r => r.status === 'flagged' || r.isFlagged) || [];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      i < rating 
        ? <StarSolidIcon key={i} style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
        : <StarIcon key={i} style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Reviews" />
      
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header with refresh */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Customer Reviews
          </h2>
          <button
            onClick={refreshData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              color: '#6b7280',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginBottom: '8px' }}>
              {renderStars(Math.round(parseFloat(averageRating)))}
            </div>
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {averageRating}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>Average Rating</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <ChatBubbleLeftIcon style={{ width: '28px', height: '28px', color: '#3b82f6', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {reviews?.length || 0}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>Total Reviews</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <FlagIcon style={{ width: '28px', height: '28px', color: '#ef4444', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {flaggedReviews.length}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>Flagged</p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Empty State */}
        {!loading && (!reviews || reviews.length === 0) && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <ChatBubbleLeftIcon style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
              No Reviews Yet
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Reviews from customers will appear here
            </p>
          </div>
        )}

        {/* Reviews List */}
        {!loading && reviews && reviews.length > 0 && (
          <>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Recent Reviews
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {reviews.map((review, index) => (
                <div
                  key={review._id || review.id || index}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderLeft: `4px solid ${(review.status === 'flagged' || review.isFlagged) ? '#ef4444' : '#10b981'}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '600', color: '#1f2937' }}>
                          {review.userName || review.user?.name || review.author || 'Anonymous'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {formatDate(review.createdAt || review.date)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '2px', marginTop: '4px' }}>
                        {renderStars(review.rating || 0)}
                      </div>
                    </div>
                    {(review.status === 'flagged' || review.isFlagged) && (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626'
                      }}>
                        Flagged
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '14px', color: '#4b5563', margin: 0, lineHeight: 1.5 }}>
                    "{review.text || review.comment || review.content || 'No comment provided'}"
                  </p>
                  
                  {/* Review photos */}
                  {review.photos && review.photos.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      {review.photos.slice(0, 4).map((photo, i) => (
                        <img 
                          key={i}
                          src={photo.url || photo}
                          alt={`Review photo ${i + 1}`}
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '8px',
                            objectFit: 'cover'
                          }}
                        />
                      ))}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Reply
                    </button>
                    <button
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                        color: '#6b7280',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Flag
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewsConsolePage;
